import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {InboxesPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useAppDispatch, useAppSelector} from '../../../../helpers/appReduxHelpers';
import {getTeamsUserIsAdminOf} from '../../../../store/data/teams/teamsSelectors';
import {showCreateLensDialog} from '../../../../store/panels/inboxes/inboxesPanelActions';
import {NavigationLineLabel} from '../../../common/navigation/navigationLineLabel';
import {NavigationLineStandardIcon} from '../../../common/navigation/navigationLineStandardIcon';
import {WorkspaceLineDropTarget} from '../line/workspaceLineDropTarget';
import {LensesWorkspaceHeaderMore} from './header/lensesWorkspaceHeaderMore';

/*
 * Props.
 */

interface CreateLensButtonProps {
  panelId: InboxesPanelId;
}

/*
 * Styles.
 */

const StyledCreateCtaDiv = styled.div`
  display: flex;
  align-items: center;
  color: ${(p) => p.theme.interactiveText};
  font-weight: ${fontWeights.medium};
`;

/*
 * Component.
 */

export const CreateLensButton: FC<CreateLensButtonProps> = (props) => {
  const dispatch = useAppDispatch();

  const teamsUserIsAdminOf = useAppSelector(getTeamsUserIsAdminOf);

  const canCreateSharedView = teamsUserIsAdminOf.length > 0;

  const onShowCreateLensDialog = () => {
    dispatch(showCreateLensDialog(props.panelId));
  };

  return (
    <UserThemeConsumer>
      {(theme) => (
        <LensesWorkspaceHeaderMore
          panelId={props.panelId}
          shouldHideDefaultWorkspaceActions
          placement="right-start"
          showCreateLensDialog={onShowCreateLensDialog}
          renderButton={(buttonProps) => (
            <div ref={buttonProps.buttonRef}>
              <WorkspaceLineDropTarget
                indentLevel={0}
                onClick={() => {
                  if (!canCreateSharedView) {
                    onShowCreateLensDialog();
                    return;
                  }

                  buttonProps.onClick();
                }}
                shouldAlphabetizeWorkspace={false}
                workspaceDragItem={undefined}
                isSelected={buttonProps.isOpen}
              >
                <NavigationLineStandardIcon iconName="plusCircleSmall" color={theme.palette.blue.shade40} />
                <NavigationLineLabel>
                  <StyledCreateCtaDiv>
                    <Trans id="lenses-workspace-create-lens">Create view</Trans>
                  </StyledCreateCtaDiv>
                </NavigationLineLabel>
              </WorkspaceLineDropTarget>
            </div>
          )}
        />
      )}
    </UserThemeConsumer>
  );
};
